import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

// Panel de análisis con IA sobre las cifras del tablero. Sólo lo ve el admin
// (además, la ruta del backend exige rol admin: esconder el botón no protege).
//
// La conversación vive aquí, en el navegador, y se manda completa en cada
// pregunta: no se guarda ningún análisis de pagos en la base.

const SUGERENCIAS = [
  '¿Cómo vamos este periodo contra el anterior?',
  '¿Qué está atorado y desde cuándo?',
  '¿Qué proveedores concentran el gasto?',
  '¿Cuánto tenemos programado por pagar?',
];

// Negritas de markdown y saltos de línea, que es lo único que suele devolver.
// No se interpreta nada más: el texto viene de un modelo y no vale la pena
// abrirle la puerta a HTML arbitrario.
function Texto({ children }) {
  const partes = String(children || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {partes.map((p, i) => p.startsWith('**') && p.endsWith('**')
        ? <b key={i}>{p.slice(2, -2)}</b>
        : <span key={i}>{p}</span>)}
    </>
  );
}

export default function AsistenteIA({ filtros }) {
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState(null);   // { configurado, modelo }
  const [mensajes, setMensajes] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [pensando, setPensando] = useState(false);
  const finRef = useRef(null);

  useEffect(() => { api.get('/asistente/estado').then(setEstado).catch(() => setEstado({ configurado: false })); }, []);
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, pensando]);

  const enviar = async (texto) => {
    const q = (texto ?? pregunta).trim();
    if (!q || pensando) return;
    setPregunta('');
    const historial = [...mensajes, { rol: 'user', texto: q }];
    setMensajes(historial);
    setPensando(true);
    try {
      const r = await api.post('/asistente', {
        pregunta: q,
        // Sin los turnos previos cada pregunta empezaría de cero y no se podría
        // decir "¿y el mes pasado?"
        historial: mensajes,
        // Lo que el usuario tiene puesto en el tablero, para que "¿por qué subió
        // esto?" tenga a qué referirse
        filtros,
      });
      setMensajes([...historial, { rol: 'assistant', texto: r.respuesta, consultas: r.consultas, uso: r.uso }]);
    } catch (e) {
      setMensajes([...historial, { rol: 'error', texto: e.message }]);
    } finally {
      setPensando(false);
    }
  };

  if (!estado) return null;

  const ANCHO = 400;

  return (
    <>
      {!abierto && (
        <button type="button" className="nx-plate" onClick={() => setAbierto(true)} title="Analizar el tablero con IA"
          style={{ width: 'auto', padding: '6px 12px', fontSize: 13, flexShrink: 0 }}>
          IA · Analizar
        </button>
      )}

      {abierto && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: `min(${ANCHO}px, 100vw)`, zIndex: 900,
          background: 'var(--panel)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          borderLeft: '1px solid rgba(0,0,0,0.10)', boxShadow: '-8px 0 32px rgba(15,23,42,0.12)',
          display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif',
        }}>
          <div style={{
            padding: '14px 16px', borderBottom: '1px solid var(--hair)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(135deg, rgba(255,84,28,0.14), rgba(255,84,28,0.04))',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>🤖 Análisis del tablero</div>
              <div style={{ fontSize: 10.5, color: 'var(--glass)' }}>
                {estado.configurado ? `${estado.modelo} · sólo lectura` : 'sin configurar'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {mensajes.length > 0 && (
                <button onClick={() => setMensajes([])} title="Empezar de nuevo"
                  style={{ border: '1px solid var(--hair)', background: 'var(--panel)', borderRadius: 7, cursor: 'pointer', fontSize: 12, padding: '4px 9px', color: 'var(--glass)' }}>🧹</button>
              )}
              <button onClick={() => setAbierto(false)} title="Cerrar"
                style={{ border: '1px solid var(--hair)', background: 'var(--panel)', borderRadius: 7, cursor: 'pointer', fontSize: 13, padding: '4px 10px', color: 'var(--ink-soft)' }}>✕</button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
            {!estado.configurado && (
              <div style={{ padding: '10px 12px', background: 'var(--danger-bg)', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12.5, color: 'var(--danger)' }}>
                Falta configurar <b>IA_API_KEY</b> en el servidor.
              </div>
            )}

            {estado.configurado && mensajes.length === 0 && (
              <div>
                <div style={{ fontSize: 12.5, color: 'var(--glass)', marginBottom: 10, lineHeight: 1.5 }}>
                  Pregunta sobre las cifras que estás viendo. Consulta las mismas agregaciones del
                  tablero, con tus mismos permisos, y no puede modificar nada.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SUGERENCIAS.map(s => (
                    <button key={s} onClick={() => enviar(s)} style={{
                      textAlign: 'left', padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 12.5,
                      border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--ink-soft)',
                      fontFamily: 'Inter, sans-serif',
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {mensajes.map((m, i) => (
              <div key={i} style={{ marginBottom: 12, display: 'flex', justifyContent: m.rol === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '92%', padding: '9px 12px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.55,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: m.rol === 'user' ? 'rgba(255,84,28,0.12)' : m.rol === 'error' ? 'var(--danger-bg)' : 'var(--hair-soft)',
                  border: `1px solid ${m.rol === 'user' ? 'rgba(255,84,28,0.28)' : m.rol === 'error' ? 'var(--danger-line)' : 'var(--hair)'}`,
                  color: m.rol === 'error' ? 'var(--danger)' : 'var(--ink)',
                }}>
                  <Texto>{m.texto}</Texto>
                  {/* Qué consultó para responder: sin esto no hay forma de saber
                      si la cifra que da corresponde a lo que preguntaste */}
                  {m.consultas?.length > 0 && (
                    <details style={{ marginTop: 7, fontSize: 10.5, color: 'var(--glass)' }}>
                      <summary style={{ cursor: 'pointer' }}>
                        {m.consultas.length} consulta{m.consultas.length !== 1 ? 's' : ''}
                        {m.uso?.total_tokens ? ` · ${m.uso.total_tokens} tokens` : ''}
                      </summary>
                      {m.consultas.map((c, j) => (
                        <div key={j} style={{ fontFamily: 'Geist Mono, monospace', marginTop: 3 }}>
                          {c.herramienta}({Object.entries(c.filtros || {}).map(([k, v]) => `${k}=${v}`).join(', ') || 'sin filtros'})
                        </div>
                      ))}
                    </details>
                  )}
                </div>
              </div>
            ))}

            {pensando && <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>Consultando las cifras…</div>}
            <div ref={finRef} />
          </div>

          <div style={{ padding: 12, borderTop: '1px solid var(--hair)', display: 'flex', gap: 8 }}>
            <textarea
              value={pregunta}
              onChange={e => setPregunta(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              placeholder={estado.configurado ? 'Pregunta algo…  (Enter para enviar)' : 'Sin configurar'}
              disabled={!estado.configurado || pensando}
              rows={2}
              style={{
                flex: 1, resize: 'none', fontSize: 12.5, padding: '8px 10px', borderRadius: 9,
                border: '1px solid var(--hair-strong)', fontFamily: 'Inter, sans-serif', outline: 'none',
              }} />
            <button onClick={() => enviar()} disabled={!estado.configurado || pensando || !pregunta.trim()}
              style={{
                border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 15, padding: '0 14px',
                background: pregunta.trim() && !pensando ? 'var(--plasma)' : 'var(--hair)',
                color: pregunta.trim() && !pensando ? 'white' : 'var(--glass)',
              }}>➤</button>
          </div>
        </div>
      )}
    </>
  );
}
